import { render } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { html, usePath, Link, navigate, lazy } from './lib.js';
import { api } from './api.js';
import { SignIn } from './ui/sign-in.js';
import { SongList } from './ui/song-list.js';
import { Feed, feedPath } from './ui/feed.js';
import { Profile } from './ui/profile.js';
import { ChooseUsername } from './ui/choose-username.js';
import { Settings } from './ui/settings.js';
import { ResetPassword, ConfirmEmail } from './ui/recovery.js';
import { Notifications, NotificationBell, useUnread } from './ui/notifications.js';
import { applyTheme, storedTheme } from './theme.js';

// The pages that play songs bring the audio engine, the instruments, the
// visuals and three.js, most of the app's code, so they load on first use.
const Editor = lazy(() => import('./ui/editor.js').then((m) => m.Editor));
const SongPage = lazy(() => import('./ui/player.js').then((m) => m.SongPage));
const EmbedPage = lazy(() => import('./ui/embed.js').then((m) => m.EmbedPage));

const EDITOR_VIEWS = 'instruments|patterns|song|visuals|publish';

/**
 * Routes:
 *   /                  the feed (everyone)
 *   /s/:id             a published song, playing live (everyone)
 *   /u/:username       a profile (everyone)
 *   /u/:username/likes the songs they've liked (everyone)
 *   /studio            your songs (signed in)
 *   /settings          theme and account (signed in)
 *   /notifications     what's happened to your songs, and new ones from people you follow (signed in)
 *   /songs/:id/:view   the editor (signed in)
 *   /sign-in           sign in, then back to ?next=
 *   /reset-password    a reset link from a recovery email (#token)
 *   /confirm-email     a recovery email's confirmation link (#token)
 *   /embed/:id         a published song's player alone, for iframes on other sites
 */
function route(path) {
  let m;
  if (path === '/') return { name: 'feed' };
  if (path === '/sign-in') return { name: 'sign-in' };
  if (path === '/reset-password') return { name: 'reset-password' };
  if (path === '/confirm-email') return { name: 'confirm-email' };
  if (path === '/studio') return { name: 'studio', auth: true };
  if (path === '/settings') return { name: 'settings', auth: true };
  if (path === '/notifications') return { name: 'notifications', auth: true };
  if ((m = /^\/s\/([0-9a-f-]+)$/.exec(path))) return { name: 'song', id: m[1] };
  if ((m = /^\/u\/([a-z0-9_]+)(?:\/(likes))?$/i.exec(path))) return { name: 'profile', username: m[1].toLowerCase(), tab: m[2] ?? 'songs' };
  if ((m = new RegExp(`^/songs/([0-9a-f-]+)(?:/(${EDITOR_VIEWS}))?$`).exec(path))) return { name: 'editor', id: m[1], view: m[2] ?? 'instruments', auth: true };
  return { name: 'missing' };
}

// The embed player stands apart from the app: no session, no header, no
// navigation, just the song in its frame.
const embed = /^\/embed\/([0-9a-f-]+)$/.exec(location.pathname);

// Everything else starts what its first page needs now, rather than after
// the first render: the session check, and alongside it the page's own
// data or code.
if (!embed) {
  api.prefetch('/auth/me');
  api.prefetch('/config');
  const first = route(location.pathname);
  if (first.name === 'feed') api.prefetch(feedPath('new'));
  if (first.name === 'song') SongPage.preload();
  if (first.name === 'editor') Editor.preload();
}

/**
 * The theme a signed-in account has chosen. An account still picking its
 * username hasn't chosen one yet (its stored theme is only the default), so
 * this device's last choice holds until it does. Signed out, the same.
 */
const accountTheme = (user) => (user?.username ? user.theme : null);

function App() {
  const path = usePath();
  const [session, setSession] = useState(null);   // { user, config } once loaded
  const [unread, setUnread] = useUnread(session?.user);

  useEffect(() => {
    Promise.all([api.get('/auth/me'), api.get('/config')])
      .then(([{ user }, config]) => {
        applyTheme(accountTheme(user) ?? storedTheme());
        setSession({ user, config });
      });
  }, []);

  if (!session) return html`<div class="loading">Loading…</div>`;

  const { user } = session;
  const setUser = (u) => {
    const theme = accountTheme(u);
    if (theme) applyTheme(theme);
    setSession((s) => ({ ...s, user: u }));
  };

  const signOut = async () => {
    await api.post('/auth/logout');
    setUser(null);
    navigate('/');
  };

  // A first sign-in isn't finished until a username is chosen.
  if (user && !user.username) {
    return html`
      <header class="site"><span class="brand">Vector<span>field</span></span></header>
      <${ChooseUsername} onChosen=${setUser} onSignOut=${signOut} />`;
  }

  const r = route(path);
  const signInHref = `/sign-in?next=${encodeURIComponent(path)}`;

  let page;
  if (r.name === 'sign-in' || (r.auth && !user)) {
    // Signed-out visitors to a signed-in page sign in right there and stay.
    const next = r.name === 'sign-in' ? new URLSearchParams(location.search).get('next') || '/studio' : path;
    page = user
      ? (navigate(next), null)
      : html`<${SignIn} config=${session.config} next=${next} onSignedIn=${(u) => { setUser(u); navigate(next); }} />`;
  } else if (r.name === 'reset-password') {
    page = html`<${ResetPassword} onSignedIn=${(u) => { setUser(u); navigate('/studio'); }} />`;
  } else if (r.name === 'confirm-email') {
    page = html`<${ConfirmEmail} user=${user} onConfirmed=${() => user && api.get('/auth/me').then((me) => setUser(me.user))} />`;
  } else if (r.name === 'feed') {
    page = html`<${Feed} user=${user} />`;
  } else if (r.name === 'studio') {
    page = html`<${SongList} />`;
  } else if (r.name === 'notifications') {
    page = html`<${Notifications} user=${user} onSeen=${() => setUnread(0)} />`;
  } else if (r.name === 'settings') {
    page = html`<${Settings} user=${user} config=${session.config} onUserChange=${setUser} onSignOut=${signOut} />`;
  } else if (r.name === 'editor') {
    page = html`<${Editor} key=${r.id} songId=${r.id} view=${r.view} />`;
  } else if (r.name === 'song') {
    page = html`<${SongPage} key=${r.id} id=${r.id} user=${user} />`;
  } else if (r.name === 'profile') {
    page = html`<${Profile} key=${r.username} username=${r.username} tab=${r.tab} user=${user} onUserChange=${setUser} />`;
  } else {
    page = html`<main class="missing"><p class="error">There's nothing at ${path}.</p><${Link} href="/">Back to the feed</${Link}></main>`;
  }

  return html`
    <header class="site">
      <${Link} href="/" class="brand">Vector<span>field</span></${Link}>
      <nav class="site-nav">
        <${Link} href="/" class=${r.name === 'feed' ? 'active' : ''}>Feed</${Link}>
        ${user && html`<${Link} href="/studio" class=${r.name === 'studio' || r.name === 'editor' ? 'active' : ''}>Studio</${Link}>`}
      </nav>
      <div class="user">
        ${user
          ? html`
            <${Link} href=${`/u/${user.username}`} class="me" title="Your profile">
              ${user.avatarUrl && html`<img src=${user.avatarUrl} alt="" referrerpolicy="no-referrer" />`}
              <span>${user.username}</span>
            </${Link}>
            <${NotificationBell} unread=${unread} active=${r.name === 'notifications'} />
            <${Link} href="/settings" class=${`settings-link ${r.name === 'settings' ? 'active' : ''}`} title="Settings">Settings</${Link}>
            <button class="ghost" onClick=${signOut}>Sign out</button>`
          : r.name !== 'sign-in' && html`<${Link} href=${signInHref} class="button primary">Sign in</${Link}>`}
      </div>
    </header>
    ${page}
  `;
}

if (embed) document.documentElement.classList.add('embedded');
render(embed ? html`<${EmbedPage} id=${embed[1]} />` : html`<${App} />`, document.getElementById('app'));
